import { useAcl } from "vue-simple-acl"

import Handlers from "@/events/handlers/index"
import { emitterEmit } from "@/plugins/mitt-emitter"
import { Endpoint } from "@/services/endpoints"
import { SubscriptionActions, subscriptionModule } from "@/store/modules/subscription.module"

export default class ServerSendEvent {
  url: string
  evtSource: EventSource
  reconnectFrequencySeconds: number
  inactiveAt: number | null
  inactiveReloadThreshold: number

  constructor() {
    this.url = Endpoint.STREAM_SUBSCRIPTION().url

    this.evtSource
    this.reconnectFrequencySeconds = 1
    this.inactiveAt = null
    this.inactiveReloadThreshold = 60 * 60 * 1000

    this.init()
  }

  init() {
    this.setupEventSource()
    this.setupLifecycleListeners()
  }

  handleReconnect() {
    this.setupEventSource()

    this.reconnectFrequencySeconds *= 2

    if (this.reconnectFrequencySeconds >= 64) {
      this.reconnectFrequencySeconds = 64
    }
  }

  async setupEventSource() {
    if (useAcl().can.not("is-email-verified")) {
      this.onEvtSourceError()

      return false
    }

    const token = await this.getToken()
    if (!token) {
      return
    }
    this.evtSource = new EventSource(`${this.url}?slt=${token}`)

    this.evtSource.onopen = () => {
      this.onEvtSourceOpen()
    }

    this.evtSource.onerror = () => {
      this.onEvtSourceError()
    }

    for (const handler of Handlers) {
      this.evtSource.addEventListener(handler.type, event =>
        handler.callback(event as MessageEvent)
      )
    }
  }

  async getToken() {
    const token = await subscriptionModule[SubscriptionActions.GET_SUBSCRIPTION_TOKEN]()
    return token
  }

  closeEventSourse() {
    this.evtSource?.close()
  }

  onEvtSourceOpen() {
    this.reconnectFrequencySeconds = 1
  }

  onEvtSourceError() {
    this.closeEventSourse()

    this.reconnectFunc()
  }

  reconnectFunc() {
    setTimeout(this.handleReconnect.bind(this), this.reconnectFrequencySeconds * 1000)
  }

  setupLifecycleListeners() {
    document.addEventListener("visibilitychange", this.onVisibilityChange)
    document.addEventListener("resume", this.reloadAfterInactiveIfNeeded)
    window.addEventListener("blur", this.onPageInactive)
    window.addEventListener("pageshow", this.reloadAfterInactiveIfNeeded)
    window.addEventListener("focus", this.reloadAfterInactiveIfNeeded)
  }

  onVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      this.onPageInactive()
      return
    }

    if (document.visibilityState === "visible") {
      this.reloadAfterInactiveIfNeeded()
    }
  }

  onPageInactive = () => {
    this.inactiveAt = Date.now()
  }

  reloadAfterInactiveIfNeeded = () => {
    if (this.wasInactiveLongEnough()) {
      emitterEmit("beforeInactiveReload")
      window.location.reload()
    }
  }

  wasInactiveLongEnough() {
    const inactiveAt = this.inactiveAt
    this.inactiveAt = null

    return inactiveAt ? Date.now() - inactiveAt > this.inactiveReloadThreshold : false
  }
}
